import { Feature } from 'geojson';
import { featureCollection } from '@turf/turf';
import { formatSoilFeatureName, WrappedColumn } from '../../utils/soils';
import { asDynamicDecimal, asPercentage } from '../../utils/formatters';
import { Column } from '../../types/table';
import { GetItemsData, SoilAttribute } from '../../types/soilAttributes';
import { getLayerArea } from '../../utils/layers';

const DEFAULT_RATING_COL = {
  displayString: 'Rating',
  accessor: 'mapUnitRatingString',
};

export interface SimpleSoilAttributeProps {
  key: string;
  ratingCol?: Column;
  ruleId: string;
  shortFormData?: { [key: string]: Nullable<string> };
  units?: string;
}

export interface SimpleSoilAttribute extends SoilAttribute {
  cols: Column[];
  getItems: (data: GetItemsData) => SimpleSoilAttributeItem[];
}

export interface SimpleSoilAttributeItem {
  areaAcres: number;
  key: string;
  mapUnitKey: string;
  mapUnitName: string;
  mapUnitRatingString: string;
  percentAoi: number;
}

const getShortFormData = (
  ruleid: string,
  shortFormData: { [key: string]: Nullable<string> } = {},
) => ({
  AggregationMethod: 'Dominant Component',
  InterpretNullsAsZero: 'False',
  TiebreakRule: '1',
  ruleid,
  ...shortFormData,
});

const getCols = (ratingCol: Column = DEFAULT_RATING_COL) => [
  {
    displayString: 'Map unit',
    accessor: 'mapUnitName',
    style: WrappedColumn,
  },
  ratingCol,
  {
    displayString: 'Acres in AOI',
    accessor: 'areaAcres',
    format: asDynamicDecimal,
  },
  {
    displayString: 'Percent of AOI',
    accessor: 'percentAoi',
    format: asPercentage,
  },
];

const getItems = ({
  data,
  thematicMapRating,
  totalAcres,
}: GetItemsData): SimpleSoilAttributeItem[] => {
  return (thematicMapRating?.Table || [])
    .map((row: string[]) => {
      const [, , mapUnitKey, mapUnitRatingString] = row;
      return { key: mapUnitKey, mapUnitKey, mapUnitRatingString };
    })
    .flatMap((row) => {
      const muKeyFeatures = data.features.filter(
        (f: Feature) => f.properties?.mapUnitKey === row.mapUnitKey,
      );
      if (muKeyFeatures.length === 0) return [];
      const muKeyAreaAcres =
        getLayerArea({}, featureCollection(muKeyFeatures)) || 0;
      return [
        {
          ...row,
          areaAcres: muKeyAreaAcres,
          mapUnitName: formatSoilFeatureName(muKeyFeatures[0]),
          percentAoi:
            totalAcres === 0 ? 0 : (muKeyAreaAcres / totalAcres) * 100,
        },
      ];
    });
};

const simpleSoilAttribute = ({
  key,
  ratingCol,
  ruleId,
  shortFormData,
  units,
}: SimpleSoilAttributeProps): SimpleSoilAttribute => ({
  attributeKey: ruleId,
  cols: getCols(ratingCol),
  getItems,
  interpRatingShortFormData: getShortFormData(ruleId, shortFormData),
  key,
  name: key,
  units,
});

export default simpleSoilAttribute;
